/**
 * useTranslationPlayback — Receives translated PCM16 audio chunks from the backend
 * and plays them gaplessly via Web Audio API.
 *
 * Used by BOTH host and guest — whoever is receiving the translated audio.
 * Host receives guest-in-translation, guest receives host-in-translation.
 */

import { useEffect, useRef, useState } from 'react';
import type { Socket } from 'socket.io-client';

interface UseTranslationPlaybackProps {
  roomId: string;
  socketRef: React.MutableRefObject<Socket | null>;
  socketConnected?: boolean; // re-registers listeners when socket becomes live
}

const TRANSLATED_SAMPLE_RATE = 24000; // OpenAI Realtime API outputs PCM16 at 24kHz (not 16kHz)
const STALE_THRESHOLD_MS = 10000;     // Drop chunks older than 10 seconds (first session can take ~5s to start)
const MIN_PLAY_AHEAD_S = 0.05;        // Schedule 50ms ahead of current time minimum

/** Decode base64 string to ArrayBuffer */
function base64ToArrayBuffer(base64: string): ArrayBuffer {
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
}

/** Convert Int16 PCM buffer to Float32 AudioBuffer */
function pcm16ToAudioBuffer(audioCtx: AudioContext, pcmBuffer: ArrayBuffer): AudioBuffer {
  const int16 = new Int16Array(pcmBuffer);
  const audioBuffer = audioCtx.createBuffer(1, int16.length, TRANSLATED_SAMPLE_RATE);
  const channelData = audioBuffer.getChannelData(0);

  for (let i = 0; i < int16.length; i++) {
    // Convert signed 16-bit int to float [-1, 1]
    channelData[i] = int16[i] / (int16[i] < 0 ? 32768 : 32767);
  }

  return audioBuffer;
}

export function useTranslationPlayback({ roomId, socketRef, socketConnected = false }: UseTranslationPlaybackProps) {
  const [isReceivingTranslation, setIsReceivingTranslation] = useState(false);

  const audioCtxRef = useRef<AudioContext | null>(null);
  const nextPlayTimeRef = useRef<number>(0);

  // Initialize AudioContext lazily on first chunk (iOS requires user gesture first,
  // which has already happened by the time translation starts)
  const getAudioCtx = (): AudioContext => {
    if (!audioCtxRef.current || audioCtxRef.current.state === 'closed') {
      audioCtxRef.current = new AudioContext({ sampleRate: TRANSLATED_SAMPLE_RATE });
    }
    return audioCtxRef.current;
  };

  useEffect(() => {
    const socket = socketRef.current;
    if (!socket) return;

    const handleTranslationChunk = async (data: { roomId: string; pcmBase64: string; sentAt: number }) => {
      if (data.roomId !== roomId) return;

      // Drop stale chunks — avoids playing delayed audio out of context
      const receiveT = Date.now();
      const age = receiveT - data.sentAt;
      console.log(`[LATENCY][useTranslationPlayback] chunk received: age=${age}ms size=${data.pcmBase64.length} t=${receiveT}`);

      if (age > STALE_THRESHOLD_MS) {
        console.warn(`[TranslationPlayback] ⚠️ Stale chunk dropped (age: ${age}ms) for room ${roomId}`);
        return;
      }

      try {
        const audioCtx = getAudioCtx();

        // Resume AudioContext if suspended (iOS autoplay policy)
        if (audioCtx.state === 'suspended') {
          await audioCtx.resume().catch((err) => console.warn('[TranslationPlayback] AudioContext resume error:', err));
        }

        const pcmBuffer = base64ToArrayBuffer(data.pcmBase64);
        if (pcmBuffer.byteLength === 0) return;

        const audioBuffer = pcm16ToAudioBuffer(audioCtx, pcmBuffer);

        // Schedule for gapless playback
        const now = audioCtx.currentTime;
        const startTime = Math.max(now + MIN_PLAY_AHEAD_S, nextPlayTimeRef.current);

        console.log(`[TranslationPlayback] Chunk: ${pcmBuffer.byteLength}B, scheduled at: ${startTime.toFixed(3)}s (age: ${age}ms)`);
        console.log(`[LATENCY][useTranslationPlayback] chunk scheduled: audioCtx.now=${now.toFixed(3)}s startTime=${startTime.toFixed(3)}s queueAhead=${(startTime - now).toFixed(3)}s`);

        const source = audioCtx.createBufferSource();
        source.buffer = audioBuffer;
        source.connect(audioCtx.destination);
        source.start(startTime);

        // Advance the play pointer
        nextPlayTimeRef.current = startTime + audioBuffer.duration;
      } catch (err: any) {
        console.error('[TranslationPlayback] Error playing audio chunk:', err.message);
      }
    };

    const handleTranslationStarted = (data: { roomId: string }) => {
      if (data.roomId !== roomId) return;
      console.log(`[TranslationPlayback] Playback started for room ${roomId}`);
      nextPlayTimeRef.current = 0; // reset schedule on new session
      setIsReceivingTranslation(true);
    };

    const handleTranslationStopped = (data: { roomId: string }) => {
      if (data.roomId !== roomId) return;
      nextPlayTimeRef.current = 0;
      setIsReceivingTranslation(false);
    };

    const handleTranslationError = (data: { roomId: string }) => {
      if (data.roomId !== roomId) return;
      nextPlayTimeRef.current = 0;
      setIsReceivingTranslation(false);
    };

    socket.on('translation-audio-chunk', handleTranslationChunk);
    socket.on('translation-started', handleTranslationStarted);
    socket.on('translation-stopped', handleTranslationStopped);
    socket.on('translation-error', handleTranslationError);

    return () => {
      socket.off('translation-audio-chunk', handleTranslationChunk);
      socket.off('translation-started', handleTranslationStarted);
      socket.off('translation-stopped', handleTranslationStopped);
      socket.off('translation-error', handleTranslationError);
    };
  }, [roomId, socketRef, socketConnected]);

  // Cleanup AudioContext on unmount
  useEffect(() => {
    return () => {
      audioCtxRef.current?.close().catch(() => {});
      audioCtxRef.current = null;
    };
  }, []);

  return { isReceivingTranslation };
}
